import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';
import { initializeSentryForFrontend } from '../sentry/instrument_frontend';
import { client as endUserClient } from '../oas/gen/quoruum/client.gen';
import { client as adminClient } from '../oas/gen/admin/client.gen';
import { client as analyticsClient } from '../oas/gen/analytics/client.gen';
import { QueryClientProvider } from '@tanstack/react-query';
import { buildLoginUrl, sanitizeReturnUrl } from '../auth/returnUrl';
import { LogLevelDesc } from '../logger';
import { queryClient } from '../api/queryClient';
import { Client } from '../oas/gen/quoruum/client';

export default function RequireSession({ orgId }: { orgId: string }): ReactElement {
  const location = useLocation();
  const [authInfo, setAuthInfo] = useState<
    | {
        apiKey: string;
        sentryDsn?: string;
        baseUrl: string;
        baseAnalyticsUrl: string;
        logLevel: LogLevelDesc;
      }
    | { fail: true }
    | null
  >(null);

  useEffect(() => {
    const checkSession = async () => {
      try {
        const authInfo = await getAuthInfo();
        if (!('fail' in authInfo)) {
          const headers = {
            'x-org-id': orgId,
          };
          initializeSentryForFrontend(authInfo.user, authInfo.logLevel, authInfo.sentryDsn);
          endUserClient.setConfig({ auth: authInfo.apiKey, baseUrl: authInfo.baseUrl, headers });
          registerIntereptors(endUserClient);
          adminClient.setConfig({ auth: authInfo.apiKey, baseUrl: authInfo.baseUrl, headers });
          registerIntereptors(adminClient);
          analyticsClient.setConfig({
            auth: authInfo.apiKey,
            baseUrl: authInfo.baseAnalyticsUrl,
            headers,
          });
          registerIntereptors(analyticsClient);
        }
        setAuthInfo(authInfo);
      } catch {
        setAuthInfo({ fail: true });
      }
    };

    void checkSession();
  }, []);

  if (authInfo === null) {
    return <div>Loading...</div>;
  }

  if ('fail' in authInfo) {
    const returnUrl = sanitizeReturnUrl(`${location.pathname}${location.search}${location.hash}`);
    return <Navigate to={buildLoginUrl(returnUrl)} replace />;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <Outlet />
    </QueryClientProvider>
  );
}

const getAuthInfo = async (): Promise<
  | {
      apiKey: string;
      user: {
        id: string;
        name: string;
      };
      sentryDsn?: string;
      baseUrl: string;
      baseAnalyticsUrl: string;
      logLevel: LogLevelDesc;
    }
  | { fail: true }
> => {
  const response = await fetch('/api/auth/validate', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    return { fail: true };
  }
  return (await response.json()) as {
    apiKey: string;
    user: {
      id: string;
      name: string;
    };
    sentryDsn?: string;
    baseUrl: string;
    baseAnalyticsUrl: string;

    logLevel: LogLevelDesc;
  };
};

const registerIntereptors = (client: Client): void => {
  client.interceptors.request.use((request) => {
    if (request.headers.get('x-skip-org-id') === 'true') {
      request.headers.delete('x-org-id');
    }

    request.headers.delete('x-skip-org-id');
    return request;
  });
};
